import { Component, computed, OnInit, Signal, signal, ChangeDetectionStrategy } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { AuthService } from '@services/auth/auth.service';
import { OnboardingService } from '@services/onboarding/onboarding.service';
import { SupportTierService } from '@services/support-tier/support-tier.service';
import { AW } from '@airwallet/types';
import { LocalizedKeys } from '@airwallet/translate/utils';
import { AwButtonComponent } from '@components/misc/aw-button/aw-button.component';

type OnboardingStepKey = 'bank_account' | 'identity' | 'plan' | 'support' | 'subscription';

type OnboardingStepDetail = { kind: 'key'; key: LocalizedKeys } | { kind: 'plan'; key: LocalizedKeys; planNameKey: LocalizedKeys; interval: number | null } | { kind: 'text'; text: string };

interface OnboardingStepDetails {
    titleKey: LocalizedKeys;
    descriptionKey: LocalizedKeys | null;
    actionKey: LocalizedKeys;
    descriptionWarning?: boolean;
    actionDisabled?: boolean;
    actionLoading?: boolean;
    editable?: boolean;
}

interface OnboardingStep {
    key: OnboardingStepKey;
    number: number;
    rowTestId: string;
    buttonTestId: string;
    status: 'done' | 'active' | 'inactive';
    titleKey: LocalizedKeys;
    detail: OnboardingStepDetail | null;
    descriptionWarning: boolean;
    actionKey: LocalizedKeys;
    actionDisabled: boolean;
    actionLoading: boolean;
    button: 'edit' | 'action' | null;
}

const stepTestIds: Record<OnboardingStepKey, string> = {
    bank_account: 'external-account',
    identity: 'kyc',
    plan: 'plan',
    support: 'support',
    subscription: 'billing'
};

const planNameKeys = {
    basic: 'subscription-plan.basic',
    smart: 'subscription-plan.smart',
    business: 'subscription-plan.business'
} as const satisfies Record<AW.Account.SubscriptionPlanType, LocalizedKeys>;

/** The "Get started" checklist shown on Home until the account can receive payouts. */
@Component({
    selector: 'app-onboarding',
    templateUrl: './onboarding.component.html',
    styleUrls: ['./onboarding.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, AwButtonComponent],
    host: { 'data-testid': 'onboarding-page', class: 'box' }
})
export class OnboardingComponent implements OnInit {
    readonly isLife: Signal<boolean> = this.onboardingService.isLife;
    private readonly planInterval: Signal<number | null> = this.onboardingService.planInterval;
    private readonly planNameKey: Signal<LocalizedKeys | null> = computed(() => {
        const planType = this.onboardingService.planType();
        return planType ? planNameKeys[planType] : null;
    });
    private readonly supportTierLabel: Signal<string | null> = this.supportTierService.supportTierLabel;

    private readonly kycLoading = signal(false);
    private readonly canEditKYC: Signal<boolean> = computed(() => this.authService.hasLimitedAccess('edit_kyc', this.authService.getRole()));

    readonly steps: Signal<OnboardingStep[]> = computed(() => {
        const capabilities = this.onboardingService.onboardingState()?.capabilities;
        const bankAccountDone = this.onboardingService.hasCompletedAccountInformation();
        const kycDone = this.onboardingService.hasCompletedKYC();
        const planDone = this.onboardingService.hasCompletedPlan();
        const supportDone = this.onboardingService.hasCompletedSupport();
        const billingDone = !!this.onboardingService.onboardingState()?.billing;
        const planNameKey = this.planNameKey();
        const supportTierLabel = this.supportTierLabel();
        const status = (done: boolean, reachable: boolean): OnboardingStep['status'] => (done ? 'done' : reachable ? 'active' : 'inactive');
        const doneDetail = (key: OnboardingStepKey): OnboardingStepDetail | null => {
            if (key === 'plan' && planNameKey) {
                const interval = this.planInterval();
                return { kind: 'plan', key: interval === 1 ? 'get-started.plan.done_monthly' : 'get-started.plan.done', planNameKey, interval };
            }
            if (key === 'support' && supportTierLabel) return { kind: 'text', text: supportTierLabel };
            return null;
        };
        const step = (key: OnboardingStepKey, stepStatus: OnboardingStep['status'], details: OnboardingStepDetails): Omit<OnboardingStep, 'number'> => {
            const editable = details.editable ?? true;
            return {
                key,
                rowTestId: `onboarding-page-list-row-${stepTestIds[key]}`,
                buttonTestId: `onboarding-page-${stepTestIds[key]}-step-button`,
                status: stepStatus,
                titleKey: details.titleKey,
                detail: details.descriptionKey ? { kind: 'key', key: details.descriptionKey } : doneDetail(key),
                descriptionWarning: details.descriptionWarning ?? false,
                actionKey: details.actionKey,
                actionDisabled: details.actionDisabled ?? false,
                actionLoading: details.actionLoading ?? false,
                button: stepStatus === 'done' ? (editable ? 'edit' : null) : stepStatus === 'active' ? 'action' : null
            };
        };

        const bankAccountStatus = status(bankAccountDone, true);
        const steps: Omit<OnboardingStep, 'number'>[] = [
            step('bank_account', bankAccountStatus, {
                titleKey: 'get-started.bank_account.title',
                descriptionKey: bankAccountStatus === 'done' ? 'get-started.bank_account.done' : 'get-started.bank_account.description',
                actionKey: 'get-started.bank_account.action'
            })
        ];

        if (this.onboardingService.shouldShowKYC()) {
            const kycStatus = status(kycDone, bankAccountDone && this.canEditKYC());
            steps.push(
                step('identity', kycStatus, {
                    titleKey: 'get-started.identity.title',
                    descriptionKey:
                        kycStatus === 'done'
                            ? 'get-started.identity.done'
                            : capabilities === 'pending'
                              ? 'get-started.identity.pending'
                              : capabilities === 'missing_document'
                                ? 'get-started.identity.missing'
                                : 'get-started.identity.description',
                    descriptionWarning: kycStatus === 'active' && capabilities === 'missing_document',
                    actionKey: capabilities === 'pending' ? 'get-started.identity.pending_action' : capabilities === 'missing_document' ? 'get-started.identity.provide' : 'get-started.identity.action',
                    actionDisabled: capabilities === 'pending',
                    actionLoading: this.kycLoading(),
                    editable: capabilities === 'active' && this.canEditKYC()
                })
            );
        }

        if (this.onboardingService.shouldShowPlanAndBilling()) {
            const planStatus = status(planDone, kycDone);
            steps.push(
                step('plan', planStatus, {
                    titleKey: 'get-started.plan.title',
                    descriptionKey: planStatus === 'done' && planNameKey ? null : 'get-started.plan.description',
                    actionKey: 'get-started.plan.action'
                })
            );
        }

        if (this.onboardingService.shouldShowSupport()) {
            const supportStatus = status(supportDone && planDone, planDone && this.supportTierService.canChoose());
            steps.push(
                step('support', supportStatus, {
                    titleKey: 'get-started.support.title',
                    descriptionKey: supportStatus === 'done' && supportTierLabel ? null : 'get-started.support.description',
                    actionKey: 'get-started.support.action',
                    editable: this.supportTierService.canChoose()
                })
            );
        }

        if (this.onboardingService.shouldShowPlanAndBilling()) {
            steps.push(
                step('subscription', status(billingDone, this.onboardingService.nextStepIsBilling()), {
                    titleKey: 'get-started.subscription.title',
                    descriptionKey: 'get-started.subscription.description',
                    actionKey: 'billing.subscribe'
                })
            );
        }

        return steps.map((onboardingStep, index) => ({ ...onboardingStep, number: index + 1 }));
    });

    readonly progress: Signal<{ done: number; total: number }> = computed(() => ({
        done: this.steps().filter(step => step.status === 'done').length,
        total: this.steps().length
    }));

    constructor(
        private router: Router,
        private onboardingService: OnboardingService,
        private authService: AuthService,
        private supportTierService: SupportTierService
    ) {}

    async ngOnInit(): Promise<void> {
        await this.onboardingService.getOnboardingStatus();
    }

    openStep(key: OnboardingStepKey): void {
        switch (key) {
            case 'bank_account':
                this.router.navigate(['account/onboarding']);
                return;
            case 'identity':
                this.goToStripeKYC();
                return;
            case 'plan':
                this.router.navigate(['account/subscription-plan']);
                return;
            case 'support':
                this.router.navigate(['account/support-tier'], { queryParams: { onboarding: true } });
                return;
            case 'subscription':
                this.router.navigate(['billing']);
                return;
            default: {
                const unhandled: never = key;
                throw new Error(`Unhandled onboarding step: ${unhandled}`);
            }
        }
    }

    private goToStripeKYC(): void {
        this.kycLoading.set(true);
        this.onboardingService.getStripeKYCUrl('/home').catch(() => this.kycLoading.set(false));
    }
}
